import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowLeft, Save, Eye, Loader2, Check, FileText } from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';
import {useClientLevel} from '../../hooks/useClientLevel';
import {jwt_decode} from '../../utils/jwt_decode';

interface DataSource {
    name: string;
    label: string;
    description: string;
    category: string;
}

interface ColumnInfo {
    name: string;
    label: string;
    type: string;
    pg_type: string;
}

const AVAILABLE_ICONS = [
    'FileText', 'Camera', 'Database', 'Network', 'Layers', 'Shield', 'Workflow', 'Activity', 'HardDrive', 'Cpu', 'Server'
];

const AVAILABLE_COLORS = [
    { name: 'blue', bg: 'bg-blue-500/10 border-blue-500/30 text-blue-400', hex: '#60a5fa' },
    { name: 'green', bg: 'bg-green-500/10 border-green-500/30 text-green-400', hex: '#4ade80' },
    { name: 'orange', bg: 'bg-orange-500/10 border-orange-500/30 text-orange-400', hex: '#fb923c' },
    { name: 'indigo', bg: 'bg-indigo-500/10 border-indigo-500/30 text-indigo-400', hex: '#818cf8' },
    { name: 'yellow', bg: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400', hex: '#facc15' },
    { name: 'purple', bg: 'bg-purple-500/10 border-purple-500/30 text-purple-400', hex: '#c084fc' },
    { name: 'rose', bg: 'bg-rose-500/10 border-rose-500/30 text-rose-400', hex: '#fb7185' },
    { name: 'cyan', bg: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400', hex: '#22d3ee' },
    { name: 'emerald', bg: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400', hex: '#34d399' }
];

const CustomReportBuilder: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const mode = searchParams.get('mode');
    const templateId = searchParams.get('id');
    const isEditMode = mode === 'edit' && templateId;
    const level = useClientLevel()
    const client_id = level.id
    const jwt = jwt_decode()
    const organization_id = Number(jwt.organization_id)

    // Report Metadata State
    const [reportName, setReportName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedIcon, setSelectedIcon] = useState('FileText');
    const [selectedColor, setSelectedColor] = useState('blue');

    // Data Source & Column Selection State
    const [dataSources, setDataSources] = useState<DataSource[]>([]);
    const [selectedSource, setSelectedSource] = useState('');
    const [columns, setColumns] = useState<ColumnInfo[]>([]);
    const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

    // Preview State
    const [previewData, setPreviewData] = useState<any[]>([]);
    const [previewHeaders, setPreviewHeaders] = useState<string[]>([]);
    const [previewLoading, setPreviewLoading] = useState(false);

    // Global loading/submitting state
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Fetch data sources
    useEffect(() => {
        const fetchDataSources = async () => {
            setIsLoading(true);
            try {
                const response = await baseapi.get('report-templates/sources');
                if (response.data.status) {
                    setDataSources(response.data.data || []);
                }
            } catch (err) {
                console.error("Failed to load data sources:", err);
                showError("Failed to load available data sources");
            } finally {
                setIsLoading(false);
            }
        };
        fetchDataSources();
    }, []);

    // Load template for edit mode
    useEffect(() => {
        const loadTemplate = async () => {
            if (!isEditMode || dataSources.length === 0) return;
            setIsLoading(true);
            try {
                const response = await baseapi.get(`report-templates/${templateId}`);
                if (response.data.status && response.data.data) {
                    const template = response.data.data;
                    setReportName(template.report_name || '');
                    setDescription(template.description || '');
                    setSelectedSource(template.data_source || '');
                    setSelectedIcon(template.icon || 'FileText');
                    setSelectedColor(template.color || 'blue');
                    
                    // columns can be template.columns or inside template.definition
                    const loadedColumns = template.columns || template.definition?.columns || [];
                    setSelectedColumns(loadedColumns);
                }
            } catch (err) {
                console.error("Failed to load template:", err);
                showError("Failed to load report template for editing");
            } finally {
                setIsLoading(false);
            }
        };
        loadTemplate();
    }, [isEditMode, templateId, dataSources]);

    // Fetch columns when data source changes
    useEffect(() => {
        const fetchColumns = async () => {
            if (!selectedSource) {
                setColumns([]);
                setSelectedColumns([]);
                return;
            }
            try {
                const response = await baseapi.get(`report-templates/sources/${selectedSource}/columns`);
                if (response.data.status) {
                    setColumns(response.data.data || []);
                    // In create mode, select all by default when selecting a source
                    if (!isEditMode) {
                        setSelectedColumns((response.data.data || []).map((c: ColumnInfo) => c.name));
                    }
                }
            } catch (err) {
                console.error("Failed to load columns:", err);
                showError("Failed to load schema for selected data source");
            }
        };
        fetchColumns();
    }, [selectedSource, isEditMode]);

    // Toggle column selection
    const handleToggleColumn = (columnName: string) => {
        setSelectedColumns(prev => {
            if (prev.includes(columnName)) {
                return prev.filter(c => c !== columnName);
            } else {
                return [...prev, columnName];
            }
        });
    };

    // Generate Preview
    const handleGeneratePreview = async () => {
        if (!selectedSource || selectedColumns.length === 0) {
            showError("Please select a data source and at least one column to preview");
            return;
        }

        setPreviewLoading(true);
        setPreviewData([]);
        setPreviewHeaders([]);

        const payload:any = {
            data_source: selectedSource,
                columns: selectedColumns,
                filters: {},
                limit: 10,
                organization_id: organization_id,
              
        }
        if(client_id){
            payload.client_id = client_id;
        }
        try {
            const response = await baseapi.post('report-templates/preview', payload);
            if (response.data.status && response.data.data) {
                const rows = response.data.data;
                setPreviewData(rows);
                if (rows.length > 0) {
                    setPreviewHeaders(Object.keys(rows[0]));
                }
            } else {
                showError("Failed to generate preview data");
            }
        } catch (err: any) {
            console.error("Failed to generate preview:", err);
            showError(err.response?.data?.message || "Failed to generate report preview");
        } finally {
            setPreviewLoading(false);
        }
    };

    // Save report template
    const handleSaveReport = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!reportName.trim()) {
            showError("Please enter a report name");
            return;
        }
        if (!selectedSource) {
            showError("Please select a data source");
            return;
        }
        if (selectedColumns.length === 0) {
            showError("Please select at least one column");
            return;
        }

        setIsSaving(true);
        try {
            const payload:any = {
                report_name: reportName.trim(),
                description: description.trim(),
                icon: selectedIcon,
                color: selectedColor,
                data_source: selectedSource,
                columns: selectedColumns,

                organization_id: organization_id,
              
            };

            if(client_id){
                payload.client_id = client_id;
            }

            let response;
            if (isEditMode) {
                response = await baseapi.put(`report-templates/${templateId}`, payload);
            } else {
                response = await baseapi.post('report-templates/', payload);
            }

            if (response.data.status) {
                showSuccess(isEditMode ? "Report template updated successfully" : "Report template created successfully");
                navigate('/reports');
            } else {
                showError("Failed to save report template");
            }
        } catch (err: any) {
            console.error("Save template error:", err);
            showError(err.response?.data?.message || "Failed to save custom report template");
        } finally {
            setIsSaving(false);
        }
    };

    const renderDynamicIcon = (iconName: string, className: string = 'w-4 h-4') => {
        const IconComponent = (Icons as any)[iconName] || Icons.FileText;
        return <IconComponent className={className} />;
    };

    const currentColorObj = AVAILABLE_COLORS.find(c => c.name === selectedColor) || AVAILABLE_COLORS[0];

    return (
        <div className="p-6 w-full min-h-screen bg-slate-900 text-slate-200 mb-20">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/reports')}
                        className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors border border-slate-800"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-lg border ${currentColorObj.bg}`}>
                            {renderDynamicIcon(selectedIcon, "w-5 h-5")}
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-white tracking-tight">
                                {isEditMode ? 'Edit Custom Report' : 'Create Custom Report'}
                            </h1>
                            <p className="text-xs text-slate-400 font-medium">Build tailored views of your system telemetry</p>
                        </div>
                    </div>
                </div>

                <button
                    onClick={handleSaveReport}
                    disabled={isSaving || isLoading || !reportName.trim() || !selectedSource || selectedColumns.length === 0}
                    className="px-5 py-2.5 bg-orange-600 hover:bg-orange-500 disabled:opacity-40 disabled:hover:bg-orange-600 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-lg shadow-orange-950/20 transition-all flex items-center gap-2"
                >
                    {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    {isEditMode ? 'Update Template' : 'Save Template'}
                </button>
            </div>

            {isLoading ? (
                <div className="py-40 flex flex-col items-center justify-center space-y-3">
                    <Loader2 className="w-10 h-10 animate-spin text-orange-500" />
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Loading configuration...</p>
                </div>
            ) : (
                <div className="flex flex-col gap-6">
                    {/* Top Row: Details, Data Source, Choose Columns */}
                    <div className="grid grid-cols-3 gap-2">
                        {/* Panel 1: Report Details */}
                        <div className="bg-slate-800/80 border border-slate-700/60 rounded-xl p-2 shadow-xl flex flex-col h-[410px]">
                            <h2 className="text-sm font-bold text-slate-300 uppercase tracking-wider border-b border-slate-700/60 pb-2 mb-3 flex items-center gap-2 shrink-0">
                                <FileText className="w-4 h-4 text-orange-500" />
                                1. Report Details
                            </h2>
                            <div className="space-y-3 overflow-y-auto pr-1 flex-1 custom-scroll">
                                <div>
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Report Name</label>
                                    <input
                                        type="text"
                                        value={reportName}
                                        onChange={(e) => setReportName(e.target.value)}
                                        placeholder="e.g. Inactive Network Assets Report"
                                        className="w-full bg-slate-900 border border-slate-700 focus:border-orange-500/50 rounded-lg px-3.5 py-2 text-sm text-white focus:outline-none transition-colors"
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Description</label>
                                    <textarea
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder="Enter details on who this report is for or what it tracks..."
                                        rows={2}
                                        className="w-full bg-slate-900 border border-slate-700 focus:border-orange-500/50 rounded-lg px-3.5 py-2 text-sm text-white focus:outline-none transition-colors resize-none"
                                    />
                                </div>

                                {/* Icon selector */}
                                <div>
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1.5">Select Icon</label>
                                    <div className="flex flex-wrap gap-2">
                                        {AVAILABLE_ICONS.map(iconName => (
                                            <button
                                                key={iconName}
                                                type="button"
                                                onClick={() => setSelectedIcon(iconName)}
                                                className={`p-2 rounded-lg border transition-all ${
                                                    selectedIcon === iconName
                                                        ? 'bg-orange-500/10 border-orange-500 text-orange-400 shadow-lg shadow-orange-950/20'
                                                        : 'bg-slate-900 border-slate-800 text-slate-500 hover:text-slate-300'
                                                }`}
                                            >
                                                {renderDynamicIcon(iconName, "w-4 h-4")}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Color Selector */}
                                <div>
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1.5">Select Color Tag</label>
                                    <div className="flex flex-wrap gap-2.5 p-1">
                                        {AVAILABLE_COLORS.map(colorObj => (
                                            <button
                                                key={colorObj.name}
                                                type="button"
                                                onClick={() => setSelectedColor(colorObj.name)}
                                                className={`w-6 h-6 rounded-full border transition-all flex items-center justify-center ${
                                                    selectedColor === colorObj.name
                                                        ? 'border-white scale-110 ring-2 ring-orange-500/55'
                                                        : 'border-transparent'
                                                }`}
                                                style={{ backgroundColor: colorObj.hex }}
                                            >
                                                {selectedColor === colorObj.name && (
                                                    <Check className="w-3.5 h-3.5 text-slate-900 stroke-[3]" />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Panel 2: Data Source */}
                        <div className="bg-slate-800/80 border border-slate-700/60 rounded-xl p-2 shadow-xl flex flex-col h-[410px]">
                            <h2 className="text-sm font-bold text-slate-300 uppercase tracking-wider border-b border-slate-700/60 pb-2 mb-3 flex items-center gap-2 shrink-0">
                                <Icons.Database className="w-4 h-4 text-orange-500" />
                                2. Data Source
                            </h2>
                            <div className="space-y-2 overflow-y-auto pr-1 flex-1 custom-scroll">
                                {dataSources.map(source => {
                                    const isSelected = selectedSource === source.name;
                                    return (
                                        <div
                                            key={source.name}
                                            onClick={() => setSelectedSource(source.name)}
                                            className={`p-3.5 rounded-xl border transition-all cursor-pointer ${
                                                isSelected
                                                    ? 'bg-slate-900 border-orange-500/80 ring-2 ring-orange-500/10'
                                                    : 'bg-slate-900/50 border-slate-800 hover:border-slate-700 hover:bg-slate-900'
                                            }`}
                                        >
                                            <div className="flex justify-between items-start mb-1">
                                                <h3 className={`text-xs font-bold ${isSelected ? 'text-orange-400' : 'text-slate-100'}`}>
                                                    {source.label}
                                                </h3>
                                                <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-slate-800 border border-slate-700 uppercase text-slate-400 tracking-wider">
                                                    {source.category}
                                                </span>
                                            </div>
                                            <p className="text-[10px] text-slate-400 leading-relaxed font-medium">{source.description}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Panel 3: Columns Selector */}
                        <div className="bg-slate-800/80 border border-slate-700/60 rounded-xl p-2 shadow-xl flex flex-col h-[410px]">
                            <h2 className="text-sm font-bold text-slate-300 uppercase tracking-wider border-b border-slate-700/60 pb-2 mb-3 flex items-center gap-2 shrink-0">
                                <Icons.Columns className="w-4 h-4 text-orange-500" />
                                3. Choose Columns
                            </h2>

                            {!selectedSource ? (
                                <p className="text-xs text-slate-500 font-medium py-2">Select a data source first to view available columns.</p>
                            ) : (
                                <div className="space-y-2 overflow-y-auto pr-1 flex-1 custom-scroll">
                                    {columns.map(col => {
                                        const isChecked = selectedColumns.includes(col.name);
                                        return (
                                            <div
                                                key={col.name}
                                                onClick={() => handleToggleColumn(col.name)}
                                                className={`flex items-center justify-between p-2.5 rounded-lg border transition-all cursor-pointer select-none ${
                                                    isChecked
                                                        ? 'bg-slate-900 border-orange-500/30 text-white'
                                                        : 'bg-slate-900/30 border-slate-800 text-slate-400 hover:bg-slate-900/60'
                                                }`}
                                            >
                                                <div className="flex items-center gap-2.5">
                                                    <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                                                        isChecked ? 'bg-orange-500 border-orange-500' : 'border-slate-600 bg-slate-950'
                                                    }`}>
                                                        {isChecked && <Check className="w-3 h-3 text-white stroke-[3]" />}
                                                    </div>
                                                    <span className="text-xs font-bold">{col.label}</span>
                                                </div>
                                                <span className="text-[9px] font-mono text-slate-500 tracking-wider uppercase bg-slate-900 px-1.5 py-0.5 rounded">
                                                    {col.type}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Bottom Row: Live Preview */}
                    <div className="w-full">
                        <div className="bg-slate-800/80 border border-slate-700/60 rounded-xl p-5 shadow-xl min-h-[420px] flex flex-col">
                            <div className="flex justify-between items-center border-b border-slate-700/60 pb-3 mb-4 shrink-0">
                                <h2 className="text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                                    <Eye className="w-4 h-4 text-orange-500" />
                                    Live Preview
                                </h2>
                                <button
                                    onClick={handleGeneratePreview}
                                    disabled={previewLoading || !selectedSource || selectedColumns.length === 0}
                                    className="px-3.5 py-1.5 bg-slate-900 hover:bg-slate-950 disabled:opacity-40 text-slate-200 hover:text-white border border-slate-700 rounded-lg text-xs font-bold tracking-wide transition-all flex items-center gap-1.5"
                                >
                                    {previewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-500" /> : <Eye className="w-3.5 h-3.5" />}
                                    Generate Live Preview
                                </button>
                            </div>

                            {previewLoading ? (
                                <div className="flex-1 flex flex-col items-center justify-center py-20 space-y-3">
                                    <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                                    <p className="text-xs font-medium text-slate-500 uppercase tracking-widest">Generating preview rows...</p>
                                </div>
                            ) : previewData.length > 0 ? (
                                <div className="flex-1 overflow-auto rounded-lg border border-slate-700/60 max-h-[380px]">
                                    <table className="w-full text-left border-collapse text-xs">
                                        <thead>
                                            <tr className="bg-slate-900 border-b border-slate-700/80 text-slate-300 font-bold">
                                                {previewHeaders.map(header => (
                                                    <th key={header} className="p-3 uppercase tracking-wider font-extrabold text-[10px]">
                                                        {header}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-800 bg-slate-900/30">
                                            {previewData.map((row, idx) => (
                                                <tr key={idx} className="hover:bg-slate-800/30 text-slate-300 font-medium">
                                                    {previewHeaders.map(header => (
                                                        <td key={header} className="p-3 truncate max-w-[200px]" title={String(row[header] || '')}>
                                                            {String(row[header] ?? '')}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="flex-1 flex flex-col items-center justify-center border border-dashed border-slate-700/60 rounded-xl p-12 text-center text-slate-500">
                                    <Icons.LayoutGrid className="w-10 h-10 mb-3 text-slate-600 stroke-[1.5]" />
                                    <p className="text-sm font-semibold mb-1">No preview generated</p>
                                    <p className="text-[11px] max-w-sm">Configure report metadata, select a database view, choose your columns, and click the preview button above to display rows dynamically.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomReportBuilder;
